import React, { useEffect, useState } from "react";
import { PageHeader } from "../../components/Shell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Plus } from "lucide-react";

export default function OrgAdmins() {
  const [admins, setAdmins] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", organization_id: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [a, o] = await Promise.all([api.get("/org-admins"), api.get("/organizations")]);
    setAdmins(a.data);
    setOrgs(o.data);
    if (!form.organization_id && o.data[0]) setForm((f) => ({ ...f, organization_id: o.data[0].id }));
  };

  useEffect(() => {
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/org-admins", form);
      toast.success("Org Admin created");
      setForm({ name: "", email: "", password: "", organization_id: orgs[0]?.id || "" });
      setShow(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="org-admins-page">
      <PageHeader
        eyebrow="Super Admin"
        title="Org Admins"
        actions={
          <button
            onClick={() => setShow((v) => !v)}
            className="px-4 py-2 rounded-md bg-[#05126E] text-white font-semibold hover:bg-[#030B45] transition-colors flex items-center gap-2"
            data-testid="open-create-admin-btn"
          >
            <Plus className="w-4 h-4" /> New admin
          </button>
        }
      />

      {show && (
        <form
          onSubmit={create}
          className="mb-8 border border-[#D2D8D4] rounded-md bg-white p-5 grid md:grid-cols-5 gap-3 items-end"
          data-testid="create-admin-form"
        >
          <Field label="Name">
            <input
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full px-3 py-2 border border-[#D2D8D4] rounded-md"
              data-testid="admin-name"
            />
          </Field>
          <Field label="Email">
            <input
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full px-3 py-2 border border-[#D2D8D4] rounded-md"
              data-testid="admin-email"
            />
          </Field>
          <Field label="Password">
            <input
              type="password"
              required
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="w-full px-3 py-2 border border-[#D2D8D4] rounded-md"
              data-testid="admin-password"
            />
          </Field>
          <Field label="Organization">
            <select
              required
              value={form.organization_id}
              onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
              className="w-full px-3 py-2 border border-[#D2D8D4] rounded-md bg-white"
              data-testid="admin-org-select"
            >
              {orgs.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
          </Field>
          <button
            type="submit"
            disabled={busy}
            className="px-4 py-2 rounded-md bg-[#0f1a14] text-white font-semibold disabled:opacity-60 hover:bg-[#1f2a24] transition-colors"
            data-testid="submit-admin"
          >
            Create
          </button>
        </form>
      )}

      <div className="border border-[#E4E4E7] bg-white">
        <table className="w-full text-sm">
          <thead className="bg-[#F7F7F8] border-b border-[#E4E4E7]">
            <tr className="text-left">
              <th className="px-4 py-3 text-[10px] font-bold uppercase tracking-widest">Name</th>
              <th className="px-4 py-3 text-[10px] font-bold uppercase tracking-widest">Email</th>
              <th className="px-4 py-3 text-[10px] font-bold uppercase tracking-widest">Organization</th>
            </tr>
          </thead>
          <tbody>
            {admins.length === 0 && (
              <tr><td colSpan={3} className="px-4 py-10 text-center text-zinc-500">No org admins yet.</td></tr>
            )}
            {admins.map((a) => (
              <tr key={a.id} className="border-b border-[#E4E4E7] hover:bg-[#F7F7F8]">
                <td className="px-4 py-3 font-semibold">{a.name}</td>
                <td className="px-4 py-3 mono text-zinc-700">{a.email}</td>
                <td className="px-4 py-3">{a.organization_name}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-widest">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
